import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Award, Search, ArrowRight, Eye, MessageSquare, Car, ChevronLeft, ChevronRight } from 'lucide-react';
import useSEO from '../../hooks/useSEO';
import { cloudinaryUrl } from '../../utils/cloudinaryUrl';
import { getLatestPosts } from '../../data/blogIndex';
import { getRecentlyViewed } from '../../utils/buyerCollections';
import { trackEvent } from '../../utils/analytics';
import './Homepage.css';
import DealerCTA from '../../components/DealerCTA';
import { vehiclePath } from '../../utils/vehicleSlug';
import { CITY_SEO } from './CityLanding';
import BrowseLinks from '../../components/BrowseLinks';
import HeroPoster from '../../components/HeroPoster';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const GAP = 16;

const SponsoredCarousel = ({ items }) => {
  const [shuffled] = useState(() => [...items].sort(() => Math.random() - 0.5));
  const [idx, setIdx] = useState(0);
  const outerRef = useRef(null);
  const [slide, setSlide] = useState({ cardW: 280, vis: 4 });

  useEffect(() => {
    const calc = () => {
      if (!outerRef.current) return;
      const w = outerRef.current.offsetWidth;
      const vis = w < 500 ? 1 : w < 800 ? 2 : 4;
      setSlide({ cardW: (w - (vis - 1) * GAP) / vis, vis });
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const max = Math.max(0, shuffled.length - slide.vis);
  if (!shuffled.length) return null;

  return (
    <section className="sp-carousel-section" data-testid="sponsored-carousel">
      <div className="section-label-row">
        <h2>Sponsored Vehicles</h2>
        <span className="sponsored-label-badge">AD</span>
      </div>
      <div className="carousel-wrapper">
        <button
          className="carousel-btn"
          onClick={() => setIdx(i => Math.max(0, i - 1))}
          disabled={idx === 0}
          aria-label="Previous"
        >
          <ChevronLeft size={22} />
        </button>
        <div className="carousel-outer" ref={outerRef}>
          <div
            className="carousel-track"
            style={{ transform: `translateX(-${idx * (slide.cardW + GAP)}px)` }}
          >
            {shuffled.map((v, i) => (
              <div key={v.id} className="carousel-slide" style={{ width: slide.cardW }}>
                {/* The first sponsored card is the homepage LCP on mobile, and it was
                    lazy: the browser held it back until layout. Eager for the cards
                    in view on first paint only. */}
                <VehicleCard v={v} tag="sponsored" source="home_sponsored" eager={i < 2} />
              </div>
            ))}
          </div>
        </div>
        <button
          className="carousel-btn"
          onClick={() => setIdx(i => Math.min(max, i + 1))}
          disabled={idx >= max}
          aria-label="Next"
        >
          <ChevronRight size={22} />
        </button>
      </div>
      <div className="carousel-dots">
        {Array.from({ length: max + 1 }, (_, i) => (
          <button
            key={i}
            className={`dot ${i === idx ? 'active' : ''}`}
            onClick={() => setIdx(i)}
            aria-label={`Go to position ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

const VehicleCard = ({ v, tag, eager, source }) => (
  <Link
    to={vehiclePath(v)}
    onClick={() => source && trackEvent('vehicle_card_click', { source, vehicle_id: v.id, vehicle_title: v.title })}
    className="vehicle-card"
    data-testid={`vehicle-card-${v.id}`}
  >
    <div className="card-img-wrap">
      <img src={cloudinaryUrl(v.images?.[0] || '/vehicle-placeholder.svg', 600)} alt={`${v.year} ${v.brand} ${v.model} used taxi for sale - ${v.city}`} loading={eager ? 'eager' : 'lazy'} fetchpriority={eager ? 'high' : undefined} />
      {v.is_featured && <div className="featured-ribbon">Featured</div>}
      {tag === 'sponsored' && <div className="sponsored-tag">Sponsored</div>}
      {v.status === 'sold' && <div className="sold-overlay">SOLD</div>}
    </div>
    <div className="card-body">
      <h3 className="card-title">{v.title}</h3>
      <div className="card-details">
        <span>{v.year}</span><span>{v.km_run?.toLocaleString()} km</span><span>{v.city}</span>
      </div>
      <div className="card-bottom">
        <span className="card-price">₹{Number(v.price_final).toLocaleString('en-IN')}</span>
        <span className={`badge-pill badge-${v.badge_type}`}>
          {v.badge_type === 'bst_listing' ? 'BST' : 'Direct Seller'}
        </span>
      </div>
    </div>
  </Link>
);

// Indexable city pages only, same source as the footer — see BrowseLinks.
const BROWSE_CITIES = Object.keys(CITY_SEO).filter((slug) => !CITY_SEO[slug].noindex);

const SkeletonCard = () => (
  <div className="vehicle-card skeleton-card" aria-hidden="true">
    <div className="card-img-wrap skeleton-box" />
    <div className="card-body">
      <div className="skeleton-line skeleton-line-title" />
      <div className="skeleton-line skeleton-line-sm" />
      <div className="skeleton-line skeleton-line-price" />
    </div>
  </div>
);

const Homepage = () => {
  useSEO({
    // EXACT string that ranked #1 for "buy sell taxi" (see below). It carries
    // the brand itself so utils/pageTitle.js returns it untouched — the
    // conditional suffix dropped " | Buy Sell Taxi" here for three days
    // (6f48faa, 2026-09-23) because the full title runs 68 characters. Do not
    // shorten it to fit: a winning title is not a truncation problem.
    title: 'Buy & Sell Used Taxis & Commercial Vehicles in India | Buy Sell Taxi',
    // Title deliberately unchanged: it ranks #1 for "buy sell taxi" at 70.6% CTR
    // and for "sell taxi car online" / "sell my taxi car" at 1.1, and there is
    // nothing to gain by risking those. The description is the lever — the old
    // one ("trusted marketplace… transparent pricing, zero middlemen") promised
    // nothing checkable, and the homepage earns 0% on a cluster of generic
    // buy-intent queries it already ranks 3.6–8.4 for ("buy taxi vehicle",
    // "used taxi cars for sale", "taxi car price in india", "buy a taxi"). What
    // demonstrably earns clicks on this site is concrete numbers: city pages run
    // 10–17% CTR, and "taxi badge cost" earns 21.4% because the page has figures.
    // ₹2 lakh is the real floor across the 82 live listings; 15 city pages exist.
    description: 'Buy and sell used taxis from ₹2 lakh — CNG Dzire, Ertiga, Innova and Eeco in Delhi NCR and across India. BST Verified or direct from the owner. Free to enquire.',
    keywords: 'buy taxi India, sell taxi India, used taxi marketplace India, used taxi for sale India, used taxi India, buy taxi online India, sell used taxi India, taxi for sale India, second hand taxi India, commercial vehicle marketplace India, used cab India, buy sell taxi',
    url: 'https://www.buyselltaxi.com',
    // Organization and WebSite are deliberately NOT declared here. index.html
    // already ships both on every route, and this page was adding a SECOND
    // Organization whose sameAs contradicted it: it claimed a Twitter profile
    // that 404s, omitted the LinkedIn and Google Business Profile links the
    // real one carries, and wrote the phone in a different format. Two
    // Organization entities disagreeing about which social accounts belong to
    // the brand is the opposite of what sameAs is for — it is the field Google
    // uses to tie the site to its profiles, and it matters more than usual here
    // because external links are the weakest signal this site has.
    // The WebSite block was a straight duplicate, SearchAction included.
    // Only genuinely homepage-specific schema stays below.
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'name': 'Commercial Vehicles for Sale in India',
        'description': 'Browse verified commercial vehicles including taxis, mini vans and buses on Buy Sell Taxi',
        'url': 'https://www.buyselltaxi.com/vehicles'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': [
          { '@type': 'Question', 'name': 'What is Buy Sell Taxi?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Buy Sell Taxi is a marketplace for used commercial vehicles in India — taxis, MPVs, vans and buses. Listings are either BST Verified, meaning our team has inspected the vehicle and its documents, or posted directly by the owner or dealer. Buyers contact sellers through an enquiry rather than a published phone number, and most of the live stock is in Delhi and Gurugram.' } },
          { '@type': 'Question', 'name': 'How do I buy a taxi or commercial vehicle on Buy Sell Taxi?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Browse our verified listings, click on a vehicle you like, and submit an enquiry. Our team follows up by call or WhatsApp to arrange a test drive and assist with paperwork.' } },
          { '@type': 'Question', 'name': 'How do I sell my taxi or van on Buy Sell Taxi?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Register as a seller, list your vehicle with photos and details, and our team will verify and publish it. We connect you with serious buyers across India.' } },
          { '@type': 'Question', 'name': 'Are all vehicles on Buy Sell Taxi verified?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Not all of them. Listings marked BST Verified are physically inspected by our team — we check the vehicle\'s condition and its documents before the listing goes live. Listings marked Direct Seller are posted by the owner or dealer, so inspect the vehicle and its papers yourself before you pay.' } },
          { '@type': 'Question', 'name': 'What types of commercial vehicles can I buy or sell?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'We specialize in taxis, cabs, mini vans, and commercial buses. All vehicle types used for commercial passenger transport are listed on our platform.' } },
          { '@type': 'Question', 'name': 'Where does Buy Sell Taxi have vehicles for sale?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Buyers and sellers anywhere in India can use Buy Sell Taxi, but most live stock is in Delhi NCR — Delhi and Gurugram — with smaller numbers of listings in cities such as Dehradun, Jaipur and Lucknow.' } },
          { '@type': 'Question', 'name': 'Is it free to list my vehicle on Buy Sell Taxi?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Basic listings are available for registered sellers. Contact us at support@buyselltaxi.com or call +91-9560759834 for details on listing packages.' } },
        ]
      }
    ]
  });

  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [recent, setRecent] = useState([]);
  const [aboutTeaser, setAboutTeaser] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [recentlyViewed] = useState(() => getRecentlyViewed());
  // Drives skeleton placeholders so async sections reserve their space and
  // don't shift the layout when data arrives (fixes CLS).
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, f, a] = await Promise.all([
          axios.get(`${API_URL}/buyer/stats`),
          axios.get(`${API_URL}/buyer/featured-listings`),
          axios.get(`${API_URL}/buyer/about`)
        ]);
        if (cancelled) return;
        setStats(s.data.stats);
        setSponsored(f.data.sponsored || []);
        setAboutTeaser(a.data.content.who_we_are);

        // Prefer curated featured listings; fall back to is_featured vehicles,
        // then to the newest listings, so the section always has content.
        let ftd = f.data.featured || [];
        if (ftd.length === 0) {
          const res = await axios.get(`${API_URL}/buyer/vehicles?sort=newest`);
          const flagged = res.data.listings.filter(v => v.is_featured);
          ftd = flagged.length > 0 ? flagged : res.data.listings.slice(0, 6);
        }
        if (!cancelled) setFeatured(ftd);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Newest listings, for the "Recently Added" row. Its own request so a
  // failure here can never blank the featured section above.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/buyer/vehicles?sort=newest&page=1`)
      .then((res) => { if (!cancelled) setRecent(res.data.listings || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const featuredIds = new Set(featured.map(v => v.id));
  const recentCards = recent.filter(v => !featuredIds.has(v.id)).slice(0, 8);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchText) params.set('search', searchText);
    if (searchCategory) params.set('category', searchCategory);
    trackEvent('search', { search_term: searchText, category: searchCategory || 'all' });
    navigate(`/vehicles?${params.toString()}`);
  };

  return (
    <div className="homepage" data-testid="homepage">
      {/* HERO — clickable poster (opens WhatsApp), then headline + search */}
      <section className="hero" data-testid="hero-section">
        <HeroPoster />
        <div className="hero-content">
          <h1>Buy &amp; Sell Used Taxis &amp; Commercial Vehicles in India</h1>
          <p className="hero-sub">India's trusted commercial vehicle marketplace — verified listings, expert support</p>
          <form className="hero-search" onSubmit={handleSearch} data-testid="hero-search">
            <input
              type="text"
              placeholder="Search by brand, model..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              data-testid="hero-search-input"
            />
            <select value={searchCategory} onChange={(e) => setSearchCategory(e.target.value)} data-testid="hero-search-category" aria-label="Vehicle category">
              <option value="">All Categories</option>
              <option value="Cars">Cars</option>
              <option value="Mini Vans">Mini Vans</option>
              <option value="Buses">Buses</option>
            </select>
            <button type="submit" className="search-btn" data-testid="hero-search-btn">
              <Search size={20} /> Search
            </button>
          </form>
        </div>
      </section>

      {/* SPONSORED CAROUSEL */}
      {sponsored.length > 0 && <SponsoredCarousel items={sponsored} />}

      {/* RECENTLY VIEWED — only for returning visitors with history */}
      {recentlyViewed.length > 0 && (
        <section className="listings-section recently-viewed-section" data-testid="recently-viewed-section">
          <h2>Recently Viewed</h2>
          <div className="vehicle-grid">
            {recentlyViewed.slice(0, 4).map(v => <VehicleCard key={v.id} v={v} source="recently_viewed" />)}
          </div>
        </section>
      )}

      {/* FEATURED LISTINGS — always rendered (skeletons while loading) so the
          section reserves its height and does not shift the page (CLS fix) */}
      {(loading || featured.length > 0) && (
        <section className="listings-section" data-testid="featured-section">
          <h2>Featured Vehicles</h2>
          <div className="vehicle-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : featured.map((v, i) => <VehicleCard key={v.id} v={v} eager={i < 4} source="home_featured" />)}
          </div>
          <Link to="/vehicles" className="view-all-btn" data-testid="view-all-featured">
            View All Vehicles <ArrowRight size={18} />
          </Link>
        </section>
      )}

      {/* RECENTLY ADDED — newest stock, minus whatever is already featured above */}
      {recentCards.length > 0 && (
        <section className="listings-section" data-testid="recent-section">
          <h2>Recently Added Taxis</h2>
          <div className="vehicle-grid">
            {recentCards.map(v => <VehicleCard key={v.id} v={v} source="home_recent" />)}
          </div>
        </section>
      )}

      {/* BROWSE — city pages, model hubs and the main tools */}
      <BrowseLinks cities={BROWSE_CITIES} />

      {/* HOW IT WORKS */}
      <section className="how-it-works" data-testid="how-it-works">
        <h2>How It Works</h2>
        <div className="steps-row">
          <div className="step">
            <div className="step-num">1</div>
            <Eye size={28} />
            <h3>Browse Verified Listings</h3>
            <p>Explore our curated collection of inspected vehicles</p>
          </div>
          <div className="step">
            <div className="step-num">2</div>
            <MessageSquare size={28} />
            <h3>Submit Your Enquiry</h3>
            <p>Tell us what you need — our team will call or WhatsApp you back</p>
          </div>
          <div className="step">
            <div className="step-num">3</div>
            <Car size={28} />
            <h3>Drive Away Happy</h3>
            <p>Complete the paperwork and drive home your new vehicle</p>
          </div>
        </div>
      </section>

      {/* Dealers buy repeatedly — worth asking before the softer about/blog content. */}
      <DealerCTA />

      {/* ABOUT US TEASER — reserve space while loading to avoid layout shift */}
      {(loading || aboutTeaser) && (
        <section className="about-teaser" data-testid="about-teaser">
          <h2>About Buy Sell Taxi</h2>
          {loading ? (
            <div className="about-skeleton">
              <div className="skeleton-line" />
              <div className="skeleton-line" />
              <div className="skeleton-line skeleton-line-short" />
            </div>
          ) : (
            <p>{aboutTeaser.substring(0, 300)}{aboutTeaser.length > 300 ? '...' : ''}</p>
          )}
          <Link to="/about" className="view-all-btn" data-testid="learn-more-about">
            Learn More About Us <ArrowRight size={18} />
          </Link>
        </section>
      )}

      {/* BLOG SECTION */}
      <section className="homepage-blog-section" data-testid="blog-section">
        <h2>Taxi Buying Guides & Tips</h2>
        <p className="homepage-blog-sub">Expert advice to help you buy, sell, and operate commercial vehicles in India</p>
        <div className="homepage-blog-grid">
          {getLatestPosts(3).map(post => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="blog-card" data-testid={`blog-card-${post.slug}`}>
              <div className="blog-card-body">
                <span className="blog-category">{post.category}</span>
                <h3 className="blog-card-title">{post.title}</h3>
                <p className="blog-card-excerpt">{post.excerpt}</p>
                <div className="blog-card-meta">
                  <span>{String(post.readTime || "").replace(/\s*read$/i, "")} read</span>
                  <span className="meta-dot">·</span>
                  <span>{new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/blog" className="view-all-blog">
          Read All Articles <ArrowRight size={16} />
        </Link>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section" data-testid="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {[
            // What BST is, stated as facts the site's data supports — the plain
            // entity definition the homepage lacked (GEO audit 2026-09-29).
            { q: 'What is Buy Sell Taxi?', a: 'Buy Sell Taxi is a marketplace for used commercial vehicles in India — taxis, MPVs, vans and buses. Listings are either BST Verified, meaning our team has inspected the vehicle and its documents, or posted directly by the owner or dealer. Buyers contact sellers through an enquiry rather than a published phone number, and most of the live stock is in Delhi and Gurugram.' },
            { q: 'How do I buy a taxi or commercial vehicle on Buy Sell Taxi?', a: 'Browse our verified listings, click on a vehicle you like, and submit an enquiry. Our team follows up by call or WhatsApp to arrange a test drive and assist with paperwork.' },
            { q: 'How do I sell my taxi or van on Buy Sell Taxi?', a: 'Register as a seller, list your vehicle with photos and details, and our team will verify and publish it. We connect you with serious buyers across India.' },
            { q: 'Are all vehicles on Buy Sell Taxi verified?', a: 'Not all of them. Listings marked BST Verified are physically inspected by our team — we check the vehicle\'s condition and its documents before the listing goes live. Listings marked Direct Seller are posted by the owner or dealer, so inspect the vehicle and its papers yourself before you pay.' },
            { q: 'What types of commercial vehicles can I buy or sell?', a: 'We specialize in taxis, cabs, mini vans, and commercial buses. All vehicle types used for commercial passenger transport are listed on our platform.' },
            { q: 'Where does Buy Sell Taxi have vehicles for sale?', a: 'Buyers and sellers anywhere in India can use Buy Sell Taxi, but most live stock is in Delhi NCR — Delhi and Gurugram — with smaller numbers of listings in cities such as Dehradun, Jaipur and Lucknow.' },
            { q: 'Is it free to list my vehicle on Buy Sell Taxi?', a: 'Basic listings are available for registered sellers. Contact us at support@buyselltaxi.com or call +91-9560759834 for details on listing packages.' },
          ].map(({ q, a }, i) => (
            <details key={i} className="faq-item">
              <summary className="faq-q">{q}</summary>
              <p className="faq-a">{a}</p>
            </details>
          ))}
        </div>
        {/* /faq had no body link from anywhere on the site. */}
        <p className="faq-more"><Link to="/faq">See all frequently asked questions</Link></p>
      </section>


    </div>
  );
};

export default Homepage;
